import { Notification } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classes from "./bookingflow-notification.module.css";

// Figma 1:2539 spans the whole preview column (1002..1452, as does the column
// itself) and sits 27px below the phone mockup (865 - 838).
const GAP_BELOW_PREVIEW = 27;
const VIEWPORT_GUTTER = 16;

export const PendingChangesNotice = ({ anchorRef, onClose }) => {

    const { t } = useTranslation();
    const [box, setBox] = useState(null);
    const noticeRef = useRef(null);

    // The preview pane goes position:fixed at md and up, so it leaves no height
    // behind for a sibling to sit under, and it sizes itself in its own effect
    // after this one has run — observing it is the only way to follow it.
    useEffect(() => {
        const wrapper = anchorRef?.current;
        // Not firstElementChild: Mantine's responsive `pos` prop injects a <style>
        // tag ahead of the pane, and measuring that gives a zero rect.
        const pane = wrapper?.querySelector(':scope > div');

        if (!wrapper || !pane) {
            return;
        }

        const place = () => {
            const column = wrapper.getBoundingClientRect();
            const height = noticeRef.current?.offsetHeight ?? 0;
            // PreviewPane scales the phone to fill the viewport, so at 100% zoom there
            // is no room below it and the card would sit off-screen for good.
            const top = Math.min(
                pane.getBoundingClientRect().bottom + GAP_BELOW_PREVIEW,
                window.innerHeight - height - VIEWPORT_GUTTER
            );

            setBox({
                top: Math.round(top),
                left: Math.round(column.left),
                width: Math.round(column.width),
            });
        };

        place();

        const observer = new ResizeObserver(place);
        observer.observe(pane);
        observer.observe(wrapper);
        if (noticeRef.current) {
            observer.observe(noticeRef.current);
        }
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place);
        };
    }, [anchorRef]);

    // Billboard has no preview column to hang under, so without one the card just
    // follows the settings panel in normal flow.
    return (
        <Notification
            ref={noticeRef}
            classNames={classes}
            icon={<IconInfoCircle size={28} />}
            title={t('booking.pendingChanges.title')}
            onClose={onClose}
            style={box ? { position: 'fixed', ...box } : undefined}
        >
            {t('booking.pendingChanges.message')}
        </Notification>
    );
}
